// Shared topic catalog: topics can be reused across roadmaps
export const topics = {
  'linux-basics': {
    id: 'linux-basics',
    title: 'Linux A to Z',
    summary: 'Comprehensive Linux track for DevOps: foundations, server setup, 200+ commands, users/groups, networking, and LVM.',
    sections: [
      {
        type: 'image',
        heading: 'Linux introduction with the architecture diagram',
        src: 'https://upload.wikimedia.org/wikipedia/commons/1/1b/Operating_system_placement.svg',
        alt: 'Linux architecture: user space, kernel space',
        caption: 'User space vs Kernel space: system calls bridge apps to hardware via the kernel.',
        description: [
          'Linux is a monolithic kernel: drivers, memory management, and scheduling live in kernel space while applications and daemons run in user space.',
          'Most day-to-day tools are GNU utilities exposed via a shell (bash, zsh). Systemd or SysV init manage services. Package managers (apt, dnf, pacman) provide software and updates.'
        ]
      },
      {
        type: 'steps',
        heading: 'How to setup Linux server using AWS EC2',
        image: 'https://upload.wikimedia.org/wikipedia/commons/5/5f/Server-icon.svg',
        description: [
          'Start with an LTS image and lock down access: only SSH from your IP, and open app ports later.',
          'Use a non-root user with sudo, rotate keys, and keep the base OS updated.'
        ],
        diagram: 'https://d1.awsstatic.com/diagrams/networking-content-delivery/Amazon-VPC-product-page-diagram_59e0fb8b6c6a2fd0d6d2b1d37abd9a1f7447e95b.png',
        diagramCaption: 'Public subnet + Security Group around EC2 in a VPC.',
        steps: [
          'Create an AWS account and a key pair (PEM) in EC2 → Key Pairs.',
          'Launch an EC2 instance: AMI = Ubuntu LTS (or Amazon Linux), t2.micro, default VPC/subnet.',
          'Security Group: allow SSH TCP 22 from your IP; later add 80/443 if you host apps.',
          'Storage: 20GB gp3 is fine to start; enable delete on termination.',
          'Launch the instance and download/save the .pem privately (chmod 400 on macOS/Linux).',
          'Connect: ssh -i your-key.pem ubuntu@<public-ip> (user is ec2-user for Amazon Linux).',
          'Update packages: Ubuntu → sudo apt update && sudo apt -y upgrade.',
          'Create a non-root user and harden SSH (optional): adduser devops && usermod -aG sudo devops.',
          'Copy your public key: ssh-copy-id or paste into ~/.ssh/authorized_keys; disable password login if desired.',
          'Install basics: git, curl, htop, ufw; enable uncomplicated firewall and open required ports.'
        ]
      },
      {
        type: 'image',
        heading: 'Typical VPC + EC2 layout',
        src: 'https://d1.awsstatic.com/diagrams/networking-content-delivery/Amazon-VPC-product-page-diagram_59e0fb8b6c6a2fd0d6d2b1d37abd9a1f7447e95b.png',
        alt: 'VPC, subnets, routes, security groups around EC2',
        caption: 'Public subnet with an EC2 instance reachable via Security Group rules.'
      },
      {
        type: 'commands',
        heading: 'Linux basic commands for DevOps engineers (100)',
        image: 'https://upload.wikimedia.org/wikipedia/commons/3/3a/Utilities-terminal.svg',
        description: 'Core navigation, text processing and permission utilities you will use every day.',
        commands: [
          { cmd: 'ls', desc: 'List directory contents', tags: ['files'], ex: 'ls -lah /var/log' },
          { cmd: 'cd', desc: 'Change directory', tags: ['files'] },
          { cmd: 'pwd', desc: 'Print working directory', tags: ['files'] },
          { cmd: 'cat file', desc: 'Concatenate/print file content', tags: ['files'], note: 'For large files, prefer less or tail -f.' },
          { cmd: 'echo "text"', desc: 'Print text or variable value', tags: ['shell'] },
          { cmd: 'touch file', desc: 'Create empty file or update timestamp', tags: ['files'] },
          { cmd: 'mkdir dir', desc: 'Create directory', tags: ['files'] },
          { cmd: 'rmdir dir', desc: 'Remove empty directory', tags: ['files'] },
          { cmd: 'rm file', desc: 'Remove file (use -r for directories)', tags: ['files'] },
          { cmd: 'cp src dst', desc: 'Copy file or directory (-r for recursive)', tags: ['files'], ex: 'cp -r /etc/nginx /backup/nginx-$(date +%F)' },
          { cmd: 'mv src dst', desc: 'Move or rename file/directory', tags: ['files'] },
          { cmd: 'find . -name "*.log"', desc: 'Find files by name, size, time, etc.', tags: ['search'] },
          { cmd: 'locate filename', desc: 'Find by database index (updatedb first)', tags: ['search'] },
          { cmd: 'updatedb', desc: 'Refresh locate database', tags: ['search'] },
          { cmd: 'head -n 10 file', desc: 'First 10 lines of file', tags: ['text'], ex: 'head -n 100 access.log' },
          { cmd: 'tail -n 10 file', desc: 'Last 10 lines of file', tags: ['text'] },
          { cmd: 'tail -f logfile', desc: 'Follow file growth (logs)', tags: ['text'] },
          { cmd: 'less file', desc: 'Pager to view file with navigation', tags: ['text'] },
          { cmd: 'more file', desc: 'Basic pager to view file', tags: ['text'] },
          { cmd: 'nano file', desc: 'Simple terminal editor', tags: ['edit'] },
          { cmd: 'vi file', desc: 'Vim editor (vi)', tags: ['edit'] },
          { cmd: 'grep PATTERN file', desc: 'Search text using regex', tags: ['search'], ex: 'grep -R "ERROR|WARN" /var/log', note: 'Use -E for extended regex, -i for case-insensitive.' },
          { cmd: 'egrep PATTERN file', desc: 'Extended grep (same as grep -E)', tags: ['search'] },
          { cmd: 'fgrep PATTERN file', desc: 'Fixed string grep (same as grep -F)', tags: ['search'] },
          { cmd: 'cut -d: -f1 /etc/passwd', desc: 'Select fields from lines', tags: ['text'] },
          { cmd: 'sort file', desc: 'Sort lines', tags: ['text'] },
          { cmd: 'uniq -c', desc: 'Unique lines with counts (works on sorted input)', tags: ['text'] },
          { cmd: 'wc -l file', desc: 'Count lines/words/bytes', tags: ['text'] },
          { cmd: 'tr a-z A-Z', desc: 'Translate characters', tags: ['text'] },
          { cmd: 'sed "s/old/new/g" file', desc: 'Stream editor for find/replace', tags: ['text'] },
          { cmd: 'awk \'{print $1}\' file', desc: 'Powerful text processing', tags: ['text'] },
          { cmd: 'xargs -n1', desc: 'Build and execute commands from input', tags: ['shell'] },
          { cmd: 'chmod 644 file', desc: 'Change permissions (r=4,w=2,x=1)', tags: ['perm'] },
          { cmd: 'chown user:group file', desc: 'Change owner/group', tags: ['perm'] },
          { cmd: 'chgrp group file', desc: 'Change group ownership', tags: ['perm'] },
          { cmd: 'umask 022', desc: 'Default permission mask', tags: ['perm'] },
          { cmd: 'stat file', desc: 'Detailed file metadata', tags: ['files'] },
          { cmd: 'file file', desc: 'Detect file type', tags: ['files'] },
          { cmd: 'du -sh *', desc: 'Disk usage per file/dir (human readable)', tags: ['disk'] },
          { cmd: 'df -h', desc: 'Filesystem free space', tags: ['disk'] },
          { cmd: 'mount', desc: 'List mounts or mount filesystem', tags: ['disk'] },
          { cmd: 'umount /mnt', desc: 'Unmount filesystem', tags: ['disk'] },
          { cmd: 'ln file link', desc: 'Create hard link (use -s for symlink)', tags: ['files'] },
          { cmd: 'tar -czf out.tgz dir', desc: 'Create tar archive (gzip)', tags: ['archive'] },
          { cmd: 'tar -xzf out.tgz', desc: 'Extract tar.gz archive', tags: ['archive'] },
          { cmd: 'gzip file', desc: 'Compress file with gzip', tags: ['archive'] },
          { cmd: 'gunzip file.gz', desc: 'Decompress gzip file', tags: ['archive'] },
          { cmd: 'zip -r out.zip dir', desc: 'Create zip archive', tags: ['archive'] },
          { cmd: 'unzip out.zip', desc: 'Extract zip archive', tags: ['archive'] },
          { cmd: 'bzip2 file', desc: 'Compress with bzip2', tags: ['archive'] },
          { cmd: 'bunzip2 file.bz2', desc: 'Decompress bzip2 file', tags: ['archive'] },
          { cmd: 'xz file', desc: 'Compress with xz', tags: ['archive'] },
          { cmd: 'unxz file.xz', desc: 'Decompress xz file', tags: ['archive'] },
          { cmd: 'curl -LO URL', desc: 'Transfer data from/to server', tags: ['network'] },
          { cmd: 'wget URL', desc: 'Non-interactive network downloader', tags: ['network'] },
          { cmd: 'ping host', desc: 'ICMP reachability test', tags: ['network'] },
          { cmd: 'traceroute host', desc: 'Trace route to host', tags: ['network'] },
          { cmd: 'dig example.com ANY', desc: 'DNS query utility', tags: ['dns'] },
          { cmd: 'host example.com', desc: 'Simple DNS lookup', tags: ['dns'] },
          { cmd: 'nslookup example.com', desc: 'Legacy DNS lookup', tags: ['dns'] },
          { cmd: 'ip a', desc: 'Show network interfaces/addresses', tags: ['network'] },
          { cmd: 'ifconfig', desc: 'Legacy interface configuration', tags: ['network'] },
          { cmd: 'netstat -tulpn', desc: 'Network stats/listening ports', tags: ['network'] },
          { cmd: 'ss -tulpn', desc: 'Socket statistics (modern netstat)', tags: ['network'] },
          { cmd: 'route -n', desc: 'View kernel routing table', tags: ['network'] },
          { cmd: 'arp -n', desc: 'View ARP cache', tags: ['network'] },
          { cmd: 'scp src user@host:dst', desc: 'Secure copy over SSH', tags: ['transfer'] },
          { cmd: 'sftp user@host', desc: 'Interactive file transfer over SSH', tags: ['transfer'] },
          { cmd: 'rsync -avz src dst', desc: 'Efficient sync/transfer', tags: ['transfer'] },
          { cmd: 'ssh user@host', desc: 'Remote shell over SSH', tags: ['ssh'] },
          { cmd: 'ssh-copy-id user@host', desc: 'Install public key on remote', tags: ['ssh'] },
          { cmd: 'whoami', desc: 'Current user', tags: ['user'] },
          { cmd: 'who', desc: 'Who is logged in', tags: ['user'] },
          { cmd: 'users', desc: 'Users currently logged in', tags: ['user'] },
          { cmd: 'id', desc: 'User and group IDs', tags: ['user'] },
          { cmd: 'groups user', desc: 'Groups for user', tags: ['user'] },
          { cmd: 'passwd', desc: 'Change user password', tags: ['user'] },
          { cmd: 'adduser user', desc: 'Add user (Debian/Ubuntu helper)', tags: ['user'] },
          { cmd: 'useradd user', desc: 'Add user (low-level)', tags: ['user'] },
          { cmd: 'usermod -aG sudo user', desc: 'Modify user (append to group)', tags: ['user'] },
          { cmd: 'userdel user', desc: 'Remove user', tags: ['user'] },
          { cmd: 'groupadd devs', desc: 'Create group', tags: ['user'] },
          { cmd: 'groupdel devs', desc: 'Delete group', tags: ['user'] },
          { cmd: 'groupmod -n new old', desc: 'Rename/modify group', tags: ['user'] },
          { cmd: 'su - user', desc: 'Switch user (start login shell)', tags: ['user'] },
          { cmd: 'sudo command', desc: 'Run command as superuser', tags: ['user'] },
          { cmd: 'visudo', desc: 'Edit sudoers safely', tags: ['user'] },
          { cmd: 'ps aux', desc: 'Process list', tags: ['proc'] },
          { cmd: 'top', desc: 'Interactive process monitor', tags: ['proc'] },
          { cmd: 'htop', desc: 'Enhanced top (install separately)', tags: ['proc'] },
          { cmd: 'kill -9 PID', desc: 'Send signal to process', tags: ['proc'] },
          { cmd: 'pkill name', desc: 'Kill by process name', tags: ['proc'] },
          { cmd: 'killall name', desc: 'Kill all processes with name', tags: ['proc'] },
          { cmd: 'jobs', desc: 'List background jobs', tags: ['shell'] },
          { cmd: 'bg %1', desc: 'Resume job in background', tags: ['shell'] },
          { cmd: 'fg %1', desc: 'Bring job to foreground', tags: ['shell'] },
          { cmd: 'nohup cmd &', desc: 'Run command immune to hangups', tags: ['shell'] },
          { cmd: 'nice -n 10 cmd', desc: 'Run with adjusted priority', tags: ['proc'] },
          { cmd: 'renice -n 10 -p PID', desc: 'Change priority of running process', tags: ['proc'] },
          { cmd: 'systemctl status service', desc: 'Inspect systemd service', tags: ['systemd'] },
          { cmd: 'service name status', desc: 'Legacy service manager wrapper', tags: ['init'] },
          { cmd: 'journalctl -u service', desc: 'View logs for a systemd unit', tags: ['logs'] }
        ]
      },
      {
        type: 'commands',
        heading: 'Linux advanced commands for DevOps (100)',
        image: 'https://upload.wikimedia.org/wikipedia/commons/3/3a/Utilities-terminal.svg',
        description: 'Advanced tooling across containers, clusters, IaC, tracing, and performance.',
        commands: [
          { cmd: 'docker ps', desc: 'List containers', tags: ['docker'] },
          { cmd: 'docker logs <ctr>', desc: 'Container logs', tags: ['docker'] },
          { cmd: 'docker exec -it <ctr> bash', desc: 'Exec into running container', tags: ['docker'] },
          { cmd: 'docker images', desc: 'List images', tags: ['docker'] },
          { cmd: 'docker-compose up -d', desc: 'Compose multi-container stack', tags: ['docker'] },
          { cmd: 'kubectl get pods -A', desc: 'List pods across namespaces', tags: ['k8s'] },
          { cmd: 'kubectl describe pod <name>', desc: 'Detailed pod information', tags: ['k8s'] },
          { cmd: 'kubectl logs <pod> -n ns', desc: 'Pod logs', tags: ['k8s'] },
          { cmd: 'kubectl exec -it <pod> -- sh', desc: 'Exec inside pod', tags: ['k8s'] },
          { cmd: 'helm install rel chart', desc: 'Install Helm chart', tags: ['k8s'] },
          { cmd: 'helm upgrade --install', desc: 'Upgrade or install release', tags: ['k8s'] },
          { cmd: 'terraform init', desc: 'Initialize Terraform working dir', tags: ['iac'] },
          { cmd: 'terraform plan', desc: 'Show execution plan', tags: ['iac'] },
          { cmd: 'terraform apply', desc: 'Provision infrastructure', tags: ['iac'] },
          { cmd: 'ansible all -m ping -i hosts', desc: 'Ad-hoc Ansible command', tags: ['config'] },
          { cmd: 'packer build template.json', desc: 'Build machine images', tags: ['images'] },
          { cmd: 'vagrant up', desc: 'Manage dev VMs locally', tags: ['vms'] },
          { cmd: 'aws ec2 describe-instances', desc: 'AWS CLI EC2 call', tags: ['aws'] },
          { cmd: 'aws s3 cp file s3://bucket/', desc: 'Upload to S3', tags: ['aws'] },
          { cmd: 'aws iam list-users', desc: 'List IAM users', tags: ['aws'] },
          { cmd: 'az vm list -o table', desc: 'Azure CLI example', tags: ['azure'] },
          { cmd: 'gcloud compute instances list', desc: 'GCP CLI example', tags: ['gcp'] },
          { cmd: 'iptables -L -n', desc: 'List iptables rules', tags: ['network'] },
          { cmd: 'nft list ruleset', desc: 'List nftables rules', tags: ['network'] },
          { cmd: 'tc qdisc show', desc: 'Traffic control info', tags: ['network'] },
          { cmd: 'nmap -sS host', desc: 'Port scan (SYN)', tags: ['network'] },
          { cmd: 'tcpdump -i eth0 port 80', desc: 'Packet capture', tags: ['network'] },
          { cmd: 'tshark -i eth0', desc: 'CLI Wireshark capture', tags: ['network'] },
          { cmd: 'lsof -i :80', desc: 'Open files/sockets by port', tags: ['diagnose'] },
          { cmd: 'strace -p PID', desc: 'Trace syscalls of process', tags: ['diagnose'] },
          { cmd: 'ltrace -p PID', desc: 'Trace library calls', tags: ['diagnose'] },
          { cmd: 'perf top', desc: 'Kernel profiling', tags: ['perf'] },
          { cmd: 'sar -n DEV 1 3', desc: 'System activity report', tags: ['perf'] },
          { cmd: 'iostat -xz 1', desc: 'CPU/Disk IO stats', tags: ['perf'] },
          { cmd: 'vmstat 1', desc: 'Virtual memory stats', tags: ['perf'] },
          { cmd: 'mpstat -P ALL 1', desc: 'Per-CPU utilization', tags: ['perf'] },
          { cmd: 'dstat -tcmnd 1', desc: 'Versatile resource stats', tags: ['perf'] },
          { cmd: 'free -m', desc: 'Memory usage (MB)', tags: ['perf'] },
          { cmd: 'atop', desc: 'Advanced system monitor', tags: ['perf'] },
          { cmd: 'iotop', desc: 'IO usage by process', tags: ['perf'] },
          { cmd: 'nethogs', desc: 'Per-process network usage', tags: ['perf'] },
          { cmd: 'ethtool eth0', desc: 'NIC driver/settings', tags: ['network'] },
          { cmd: 'sysctl -a', desc: 'Kernel runtime parameters', tags: ['kernel'] },
          { cmd: 'ulimit -a', desc: 'Shell resource limits', tags: ['shell'] },
          { cmd: 'udevadm info -a -p /sys', desc: 'Device manager info', tags: ['devices'] },
          { cmd: 'dmsetup ls', desc: 'Device mapper status', tags: ['storage'] },
          { cmd: 'mdadm --detail /dev/md0', desc: 'Manage software RAID', tags: ['storage'] },
          { cmd: 'pvcreate /dev/xvdf', desc: 'Create LVM physical volume', tags: ['lvm'] },
          { cmd: 'vgcreate vg0 /dev/xvdf', desc: 'Create volume group', tags: ['lvm'] },
          { cmd: 'lvcreate -n data -L 10G vg0', desc: 'Create logical volume', tags: ['lvm'] },
          { cmd: 'lvs; vgs; pvs', desc: 'List LVM objects', tags: ['lvm'] },
          { cmd: 'resize2fs /dev/vg0/data', desc: 'Grow ext filesystem', tags: ['storage'] },
          { cmd: 'xfs_growfs /mnt', desc: 'Grow XFS filesystem', tags: ['storage'] },
          { cmd: 'mount --bind /src /dst', desc: 'Bind mount a directory', tags: ['storage'] },
          { cmd: 'chroot /mnt/sysroot', desc: 'Change root directory', tags: ['recovery'] },
          { cmd: 'nsenter --target PID --mount --uts --ipc --net --pid', desc: 'Enter namespaces', tags: ['namespaces'] },
          { cmd: 'unshare --mount --uts --ipc --net --pid --fork', desc: 'Run in new namespaces', tags: ['namespaces'] },
          { cmd: 'systemd-run --unit demo --scope sleep 60', desc: 'Run transient units', tags: ['systemd'] },
          { cmd: 'systemd-analyze blame', desc: 'Boot-up time per service', tags: ['systemd'] },
          { cmd: 'systemd-cgls', desc: 'Show cgroup tree', tags: ['cgroups'] },
          { cmd: 'systemd-cgtop', desc: 'Top for cgroups', tags: ['cgroups'] },
          { cmd: 'journalctl -f', desc: 'Follow system journal', tags: ['logs'] },
          { cmd: 'journalctl -u nginx', desc: 'Logs for a service', tags: ['logs'] },
          { cmd: 'rsync -avz src dst', desc: 'Archive sync with compression', tags: ['transfer'] },
          { cmd: 'crontab -e', desc: 'Edit user cron table', tags: ['schedule'] },
          { cmd: 'at now + 5 minutes', desc: 'Schedule one-off job', tags: ['schedule'] },
          { cmd: 'date -d "@<epoch>"', desc: 'Humanize timestamp', tags: ['time'] },
          { cmd: 'timedatectl set-ntp true', desc: 'Enable NTP time sync', tags: ['time'] },
          { cmd: 'localectl status', desc: 'Locale settings', tags: ['system'] },
          { cmd: 'openssl s_client -connect host:443', desc: 'TLS troubleshooting', tags: ['security'] },
          { cmd: 'ssh-keygen -t ed25519', desc: 'Generate SSH key pair', tags: ['security'] },
          { cmd: 'gpg --list-keys', desc: 'GnuPG key management', tags: ['security'] },
          { cmd: 'fail2ban-client status', desc: 'Ban rule status', tags: ['security'] },
          { cmd: 'ufw allow 22/tcp', desc: 'Uncomplicated Firewall rule', tags: ['security'] },
          { cmd: 'firewall-cmd --list-all', desc: 'firewalld zone config', tags: ['security'] },
          { cmd: 'semanage boolean -l', desc: 'SELinux: booleans', tags: ['selinux'] },
          { cmd: 'setenforce 1', desc: 'SELinux enforcing mode', tags: ['selinux'] },
          { cmd: 'sealert -a /var/log/audit/audit.log', desc: 'SELinux audit analysis', tags: ['selinux'] },
          { cmd: 'apparmor_status', desc: 'AppArmor status', tags: ['security'] },
          { cmd: 'auditctl -s', desc: 'Audit framework status', tags: ['security'] },
          { cmd: 'ausearch -m AVC', desc: 'Query audit logs', tags: ['security'] },
          { cmd: 'jq \'..|.error? // empty\' file.json', desc: 'Query JSON', tags: ['text'] },
          { cmd: 'yq \'.. | select(tag=="!!str")\'', desc: 'Query YAML', tags: ['text'] },
          { cmd: 'envsubst < in.tmpl > out', desc: 'Substitute env vars in templates', tags: ['text'] },
          { cmd: 'sponge file', desc: 'Read and write file via pipe', tags: ['text'] },
          { cmd: 'parallel -j4 ::: a b c', desc: 'Run jobs in parallel', tags: ['perf'] },
          { cmd: 'http GET https://example.com', desc: 'HTTPie friendly HTTP client', tags: ['network'] },
          { cmd: 'curl -I https://example.com', desc: 'Fetch only response headers', tags: ['network'] },
          { cmd: 'wget --mirror URL', desc: 'Mirror site', tags: ['network'] },
          { cmd: 'nc -lvnp 9000', desc: 'Netcat: listen/transfer/debug', tags: ['network'] },
          { cmd: 'socat TCP-LISTEN:9000,reuseaddr,fork FILE:out', desc: 'Socket cat swiss army knife', tags: ['network'] },
          { cmd: 'iperf3 -s | iperf3 -c host', desc: 'Network throughput test', tags: ['network'] },
          { cmd: 'haproxy -c -f haproxy.cfg', desc: 'Validate HAProxy config', tags: ['proxy'] },
          { cmd: 'nginx -t', desc: 'Test Nginx configuration', tags: ['web'] },
          { cmd: 'apachectl -t', desc: 'Test Apache configuration', tags: ['web'] },
          { cmd: 'certbot certonly --standalone -d domain', desc: 'Issue TLS cert with Let\'s Encrypt', tags: ['security'] }
        ]
      },
      {
        heading: 'Users and group management in Linux',
        image: 'https://upload.wikimedia.org/wikipedia/commons/9/99/OOjs_UI_icon_userGroup-ltr.svg',
        description: [
          'Users own files and processes; groups aggregate permissions to simplify policy.',
          'Use sudo for controlled privilege escalation. Edit sudoers via visudo to avoid syntax errors.'
        ],
        diagram: 'https://upload.wikimedia.org/wikipedia/commons/3/3a/Unix_groups_example.svg',
        items: [
          'Users authenticate and execute processes; groups aggregate permissions for sets of users.',
          '`/etc/passwd` maps user accounts to UIDs/GIDs; `/etc/shadow` stores password hashes.',
          'Grant privilege with groups like `sudo` (Debian/Ubuntu) or `wheel` (RHEL/Fedora).',
          'Principles: least privilege, strong SSH keys, audited sudo (use `visudo`).'
        ]
      },
      {
        heading: 'File management in Linux',
        image: 'https://upload.wikimedia.org/wikipedia/commons/9/96/Folder-orange.svg',
        description: [
          'Everything is a file. Learn inode metadata, link types, and filesystem layout.',
          'Prefer rsync for incremental backups and verify with checksums for integrity.'
        ],
        items: [
          'Everything is a file: devices under `/dev`, processes under `/proc`, sysfs under `/sys`. ',
          'Know inodes and hard vs soft links; use `ln` for links and `stat` to inspect metadata.',
          'Backups with `tar`, `rsync`, and incremental snapshots (LVM snapshots or btrfs/zfs).'
        ]
      },
      {
        type: 'commands',
        heading: 'File transfer commands',
        image: 'https://upload.wikimedia.org/wikipedia/commons/6/6b/Antu-folder-remote.svg',
        description: 'Use SSH-backed transfer for security. Rsync can resume and verify integrity.',
        commands: [
          { cmd: 'scp -i key.pem file ubuntu@host:/path/', desc: 'Copy file over SSH with key auth', ex: 'scp -i ~/.ssh/key.pem build.tgz ubuntu@54.1.2.3:/opt/app/', note: 'Use -C for compression over slow links.' },
          { cmd: 'rsync -avzP src/ ubuntu@host:/path/', desc: 'Fast incremental sync with progress', ex: 'rsync -avzP --delete dist/ ubuntu@host:/var/www/app/', note: 'Add --delete to mirror exactly; be careful!' },
          { cmd: 'sftp -i key.pem ubuntu@host', desc: 'Interactive SFTP session' }
        ]
      },
      {
        type: 'commands',
        heading: 'Linux networking commands',
        image: 'https://upload.wikimedia.org/wikipedia/commons/3/3b/Antu-network-transmit-receive.svg',
        description: 'These tools help debug connectivity, routing, DNS, and sockets.',
        commands: [
          { cmd: 'ip a; ip r', desc: 'Addresses and routes' },
          { cmd: 'ss -tulpn', desc: 'Listening sockets by process' },
          { cmd: 'dig +trace example.com', desc: 'Full DNS resolution path' },
          { cmd: 'traceroute example.com', desc: 'Path across routers' }
        ]
      },
      {
        type: 'commands',
        heading: 'Pro Linux commands: AWK, GREP, FIND, SED',
        image: 'https://upload.wikimedia.org/wikipedia/commons/0/01/GNU_bash_logo.svg',
        description: 'Powerful text processing for logs and data pipelines. Compose them with pipes.',
        commands: [
          { cmd: 'grep -R "error" /var/log', desc: 'Recursive search for text', ex: 'grep -R -n "(ERROR|FATAL)" /var/log', note: 'Use -n for line numbers and -H to always print filenames.' },
          { cmd: 'find /var/log -type f -mtime -1', desc: 'Find files by type/time', ex: 'find /var/log -type f -size +10M -delete', note: 'Combine predicates to build cleanup jobs. Always test with -print first.' },
          { cmd: 'sed -n "1,10p" file', desc: 'Print selected lines', ex: 'sed -E "s/(\\d{4})-(\\d{2})-(\\d{2})/\\3-\\2-\\1/g" dates.txt' },
          { cmd: "awk -F, '{sum+=$3} END{print sum}' file.csv", desc: 'Aggregate CSV column', ex: "awk -F, 'NR>1 {bytes+=$5} END{print bytes/1024/1024 \" MB\"}' access.csv" }
        ]
      },
      {
        heading: 'Linux volume management',
        image: 'https://upload.wikimedia.org/wikipedia/commons/8/8a/OOjs_UI_icon_database.svg',
        description: 'Disks → partitions → LVM (PV/VG/LV) → filesystem → mountpoint. Use UUIDs in fstab for reliable mounts.',
        items: [
          'Disks → partitions → filesystems → mounts; choose ext4/XFS for general purpose.',
          'Use UUIDs in /etc/fstab for stable mounts; consider RAID (mdadm) for redundancy.'
        ]
      },
      {
        type: 'commands',
        heading: 'LVM Linux Volume Manager',
        image: 'https://images.unsplash.com/photo-1516245834210-c4c142787335?auto=format&fit=crop&w=1200&q=60',
        description: 'Create PVs on disks, group into VGs, and carve flexible LVs. Grow online where supported.',
        commands: [
          { cmd: 'pvcreate /dev/xvdf', desc: 'Create physical volume' },
          { cmd: 'vgcreate vg0 /dev/xvdf', desc: 'Create volume group vg0' },
          { cmd: 'lvcreate -n data -L 10G vg0', desc: 'Create 10G logical volume', ex: 'lvcreate -n data -L 10G vg0\nmkfs.ext4 /dev/vg0/data\nmount /dev/vg0/data /mnt/data' },
          { cmd: 'mkfs.ext4 /dev/vg0/data', desc: 'Make filesystem' },
          { cmd: 'mount /dev/vg0/data /mnt', desc: 'Mount LV', ex: 'mkdir -p /mnt/data && mount /dev/vg0/data /mnt/data' },
          { cmd: 'lvextend -L +5G /dev/vg0/data && resize2fs /dev/vg0/data', desc: 'Online grow filesystem', note: 'For XFS use xfs_growfs <mountpoint> instead of resize2fs.' }
        ]
      }
    ]
  },
  'git-and-github': {
    id: 'git-and-github',
    title: 'Git & GitHub',
    summary: 'Version control, branching workflows, pull requests, and CI triggers.',
    sections: [
      {
        heading: 'What you will learn',
        items: [
          'git init/clone/add/commit/push/pull',
          'Branching, merging, rebasing, resolving conflicts',
          'Pull Requests, code reviews, GitHub Actions basics'
        ]
      },
      {
        heading: 'Hands-on',
        items: ['Create a repo, branch strategy, and a PR pipeline']
      },
      {
        heading: 'Resources',
        items: [
          '[Pro Git Book](https://git-scm.com/book/en/v2)',
          '[GitHub Docs](https://docs.github.com/en)'
        ]
      }
    ]
  },
  'networking-fundamentals': {
    id: 'networking-fundamentals',
    title: 'Networking Fundamentals',
    summary: 'OSI model, TCP/UDP, DNS, HTTP, load balancers, VPCs.',
    sections: [
      { heading: 'What you will learn', items: ['OSI layers', 'IP, TCP/UDP, ports', 'DNS, HTTP/HTTPS, proxies, LB'] },
      { heading: 'Hands-on', items: ['Use curl, dig, traceroute to diagnose issues'] },
      { heading: 'Resources', items: ['[Cloudflare Learn](https://www.cloudflare.com/learning/)'] }
    ]
  },
  'programming-python-basics': {
    id: 'programming-python-basics',
    title: 'Programming Basics (Python)',
    summary: 'Syntax, control flow, functions, modules, virtualenv, packaging.',
    sections: [
      { heading: 'What you will learn', items: ['Types, control flow, functions, OOP', 'venv, pip, packaging', 'pytest basics'] },
      { heading: 'Hands-on', items: ['Build a CLI utility and unit tests'] },
      { heading: 'Resources', items: ['[Python Tutor](https://pythontutor.com/)', '[Real Python](https://realpython.com/)'] }
    ]
  },
  'data-structures-algorithms': {
    id: 'data-structures-algorithms',
    title: 'Data Structures & Algorithms',
    summary: 'Complexity, arrays, linked lists, trees, graphs, DP, greedy.',
    sections: [
      { heading: 'What you will learn', items: ['Big-O notation', 'Classic DS (stack/queue/tree/heap)', 'Search/sort, DP, graph algos'] },
      { heading: 'Hands-on', items: ['Solve problems and analyze complexity'] },
      { heading: 'Resources', items: ['[CP Handbook](https://cses.fi/book/index.html)'] }
    ]
  },
  'system-design-basics': {
    id: 'system-design-basics',
    title: 'System Design Basics',
    summary: 'Scalability, caching, databases, partitions, queues, microservices.',
    sections: [
      { heading: 'What you will learn', items: ['CAP, sharding/replication', 'CDN, cache, message queues', 'Observability, resiliency'] },
      { heading: 'Hands-on', items: ['Design a rate limiter and a feed system'] },
      { heading: 'Resources', items: ['[Awesome System Design](https://github.com/madd86/awesome-system-design)'] }
    ]
  },
  'sql-and-databases': {
    id: 'sql-and-databases',
    title: 'SQL & Databases',
    summary: 'Relational modeling, joins, indexing, transactions, NoSQL overview.',
    sections: [
      { heading: 'What you will learn', items: ['Normalization', 'Joins, windows', 'Indexes, transactions, isolation'] },
      { heading: 'Hands-on', items: ['Write queries for analytics and OLTP cases'] },
      { heading: 'Resources', items: ['[Use The Index, Luke](https://use-the-index-luke.com/)'] }
    ]
  },
  'cloud-basics': {
    id: 'cloud-basics',
    title: 'Cloud Basics',
    summary: 'IaaS vs PaaS vs SaaS, regions/AZs, compute, storage, networking.',
    sections: [
      { heading: 'What you will learn', items: ['EC2/VMs, containers, functions', 'Object/block/file storage', 'VPC, subnets, security groups'] },
      { heading: 'Hands-on', items: ['Deploy a simple app to any cloud'] },
      { heading: 'Resources', items: ['[12-Factor](https://12factor.net/)'] }
    ]
  },
  'docker': {
    id: 'docker',
    title: 'Docker',
    summary: 'Images, containers, Dockerfiles, Compose, registries.',
    sections: [
      { heading: 'What you will learn', items: ['Dockerfile best practices', 'Compose for local stacks', 'Image scanning, slim images'] },
      { heading: 'Hands-on', items: ['Containerize a web app + DB with Compose'] },
      { heading: 'Resources', items: ['[Docker Docs](https://docs.docker.com/)'] }
    ]
  },
  'kubernetes': {
    id: 'kubernetes',
    title: 'Kubernetes',
    summary: 'Pods, Deployments, Services, Ingress, ConfigMaps, Helm.',
    sections: [
      { heading: 'What you will learn', items: ['Workloads, Services/Ingress', 'Config/Secrets', 'Helm basics'] },
      { heading: 'Hands-on', items: ['Deploy a canary rollout with Helm'] },
      { heading: 'Resources', items: ['[Kubernetes Docs](https://kubernetes.io/docs/)'] }
    ]
  },
  'ci-cd': {
    id: 'ci-cd',
    title: 'CI/CD',
    summary: 'Build, test, deploy pipelines; gating; environments; rollback.',
    sections: [
      { heading: 'What you will learn', items: ['Workflows & runners', 'Artifact mgmt', 'Blue/green & canary deploys'] },
      { heading: 'Hands-on', items: ['Create a pipeline with tests + deploy'] },
      { heading: 'Resources', items: ['[GitHub Actions](https://docs.github.com/actions)'] }
    ]
  },
  'terraform': {
    id: 'terraform',
    title: 'Terraform',
    summary: 'IaC, providers, modules, state mgmt, workspaces, policies.',
    sections: [
      { heading: 'What you will learn', items: ['Providers, resources', 'Modules and state', 'Plan/apply, workspaces'] },
      { heading: 'Hands-on', items: ['Provision a VPC and managed DB'] },
      { heading: 'Resources', items: ['[Terraform Docs](https://developer.hashicorp.com/terraform/docs)'] }
    ]
  },
  'monitoring-logging': {
    id: 'monitoring-logging',
    title: 'Monitoring & Logging',
    summary: 'Metrics, traces, logs, SLOs, alerting, dashboards.',
    sections: [
      { heading: 'What you will learn', items: ['Metrics vs logs vs traces', 'SLOs/error budgets', 'Dashboards & on-call basics'] },
      { heading: 'Hands-on', items: ['Instrument a service and add alerts'] },
      { heading: 'Resources', items: ['[OpenTelemetry](https://opentelemetry.io/)'] }
    ]
  },
  'machine-learning-fundamentals': {
    id: 'machine-learning-fundamentals',
    title: 'Machine Learning Fundamentals',
    summary: 'Supervised/unsupervised learning, feature engineering, model evaluation.',
    sections: [
      { heading: 'What you will learn', items: ['Bias/variance', 'Cross-validation', 'Regularization'] },
      { heading: 'Hands-on', items: ['Train/test a model on a tabular dataset'] },
      { heading: 'Resources', items: ['[scikit-learn](https://scikit-learn.org/stable/)'] }
    ]
  },
  'math-for-ml': {
    id: 'math-for-ml',
    title: 'Math for ML',
    summary: 'Linear algebra, calculus, probability, statistics essentials.',
    sections: [
      { heading: 'What you will learn', items: ['Vectors/matrices', 'Derivatives/gradients', 'Probability & stats basics'] },
      { heading: 'Resources', items: ['[Khan Academy](https://www.khanacademy.org/)'] }
    ]
  },
  'deep-learning-basics': {
    id: 'deep-learning-basics',
    title: 'Deep Learning Basics',
    summary: 'Neural nets, backprop, CNNs/RNNs, modern frameworks.',
    sections: [
      { heading: 'What you will learn', items: ['Layers/activation', 'Backpropagation', 'CNN/RNN overview'] },
      { heading: 'Hands-on', items: ['Build a simple classifier in PyTorch or TF'] },
      { heading: 'Resources', items: ['[PyTorch](https://pytorch.org/)', '[TensorFlow](https://www.tensorflow.org/)'] }
    ]
  },
  'nlp-basics': {
    id: 'nlp-basics',
    title: 'NLP Basics',
    summary: 'Tokenization, embeddings, classic models, evaluation metrics.',
    sections: [
      { heading: 'What you will learn', items: ['Tokenizers', 'Word embeddings', 'Classification & NER'] },
      { heading: 'Hands-on', items: ['Train a text classifier'] },
      { heading: 'Resources', items: ['[Hugging Face Course](https://huggingface.co/learn)'] }
    ]
  },
  'genai-fundamentals': {
    id: 'genai-fundamentals',
    title: 'Generative AI Fundamentals',
    summary: 'LLMs, diffusion, transformers, pretraining vs finetuning.',
    sections: [
      { heading: 'What you will learn', items: ['Transformer architecture', 'Inference vs finetuning', 'Evaluation/safety basics'] },
      { heading: 'Hands-on', items: ['Run inference with an open LLM'] },
      { heading: 'Resources', items: ['[Transformer Paper](https://arxiv.org/abs/1706.03762)'] }
    ]
  },
  'prompt-engineering': {
    id: 'prompt-engineering',
    title: 'Prompt Engineering',
    summary: 'Prompt patterns, evaluation, retrieval augmentation basics.',
    sections: [
      { heading: 'What you will learn', items: ['Prompt patterns', 'RAG basics', 'Guardrails/evaluation'] },
      { heading: 'Hands-on', items: ['Build a simple RAG app'] },
      { heading: 'Resources', items: ['[Prompting Guide](https://www.promptingguide.ai/)'] }
    ]
  },
  'llmops': {
    id: 'llmops',
    title: 'LLMOps',
    summary: 'Observability, evaluation, cost/perf trade-offs, deployment patterns.',
    sections: [
      { heading: 'What you will learn', items: ['Eval harnesses', 'Tracing/telemetry', 'Latency/cost tuning'] },
      { heading: 'Resources', items: ['[LangSmith Docs](https://docs.smith.langchain.com/)'] }
    ]
  },
  'power-bi-fundamentals': {
    id: 'power-bi-fundamentals',
    title: 'Power BI Fundamentals',
    summary: 'Reports, dashboards, data sources, Power Query, publishing.',
    sections: [
      { heading: 'What you will learn', items: ['Import vs DirectQuery', 'Layouts and visuals', 'Publishing/sharing'] },
      { heading: 'Resources', items: ['[Power BI Docs](https://learn.microsoft.com/power-bi/)'] }
    ]
  },
  'data-modeling': {
    id: 'data-modeling',
    title: 'Data Modeling',
    summary: 'Star/snowflake schemas, relationships, one-to-many, bi-directional filters.',
    sections: [
      { heading: 'Hands-on', items: ['Design a star schema for sales analytics'] },
      { heading: 'Resources', items: ['[Kimball Toolkit](https://www.kimballgroup.com/data-warehouse-business-intelligence-resources/)'] }
    ]
  },
  'power-query': {
    id: 'power-query',
    title: 'Power Query',
    summary: 'Data transformation with M, merging, appending, cleaning data.',
    sections: [
      { heading: 'Resources', items: ['[Power Query Docs](https://learn.microsoft.com/power-query/)'] }
    ]
  },
  'dax-basics': {
    id: 'dax-basics',
    title: 'DAX Basics',
    summary: 'Measures vs columns, time intelligence, CALCULATE, filter context.',
    sections: [
      { heading: 'Hands-on', items: ['Build measures for YoY growth and retention'] },
      { heading: 'Resources', items: ['[DAX Guide](https://dax.guide/)'] }
    ]
  },
  'frontend-fundamentals': {
    id: 'frontend-fundamentals',
    title: 'Frontend Fundamentals',
    summary: 'HTML, CSS, JavaScript, accessibility, responsive design.',
    sections: [
      { heading: 'Resources', items: ['[MDN Web Docs](https://developer.mozilla.org/)'] }
    ]
  },
  'react-basics': {
    id: 'react-basics',
    title: 'React Basics',
    summary: 'Components, hooks, state mgmt, routing, forms, testing.',
    sections: [
      { heading: 'Hands-on', items: ['Build a CRUD app with routing'] },
      { heading: 'Resources', items: ['[React Docs](https://react.dev/)'] }
    ]
  },
  'backend-node-express': {
    id: 'backend-node-express',
    title: 'Backend (Node & Express)',
    summary: 'REST APIs, middleware, auth, validation, testing.',
    sections: [
      { heading: 'Hands-on', items: ['Create an Express API with JWT auth'] },
      { heading: 'Resources', items: ['[Express Docs](https://expressjs.com/)'] }
    ]
  },
  'api-design-rest': {
    id: 'api-design-rest',
    title: 'API Design (REST)',
    summary: 'Resource modeling, pagination, versioning, error handling.',
    sections: [
      { heading: 'Resources', items: ['[Microsoft REST Guidelines](https://github.com/microsoft/api-guidelines)'] }
    ]
  },
  'security-fundamentals': {
    id: 'security-fundamentals',
    title: 'Security Fundamentals',
    summary: 'AuthN/AuthZ, OWASP Top 10, secrets mgmt, least privilege.',
    sections: [
      { heading: 'Resources', items: ['[OWASP Top 10](https://owasp.org/www-project-top-ten/)'] }
    ]
  },
  'testing-automation': {
    id: 'testing-automation',
    title: 'Testing & Automation',
    summary: 'Unit, integration, e2e, CI integration, flaky test triage.',
    sections: [
      { heading: 'Resources', items: ['[Testing Trophy](https://kentcdodds.com/blog/the-testing-trophy)'] }
    ]
  },
  'aws-fundamentals': {
    id: 'aws-fundamentals',
    title: 'AWS Fundamentals',
    summary: 'EC2, S3, RDS, IAM, VPC basics, CloudWatch.',
    sections: [
      { heading: 'Resources', items: ['[AWS Ramp-Up](https://aws.amazon.com/training/ramp-up/)'] }
    ]
  },
  'azure-fundamentals': {
    id: 'azure-fundamentals',
    title: 'Azure Fundamentals',
    summary: 'VMs, Storage, SQL, VNets, RBAC, Monitor.',
    sections: [
      { heading: 'Resources', items: ['[Microsoft Learn](https://learn.microsoft.com/azure/)'] }
    ]
  },
  'gcp-fundamentals': {
    id: 'gcp-fundamentals',
    title: 'GCP Fundamentals',
    summary: 'GCE, GCS, GKE, IAM, VPC, Cloud Logging.',
    sections: [
      { heading: 'Resources', items: ['[Google Cloud Skills](https://cloud.google.com/training)'] }
    ]
  },
  'data-engineering-fundamentals': {
    id: 'data-engineering-fundamentals',
    title: 'Data Engineering Fundamentals',
    summary: 'ETL/ELT, batch vs streaming, lakehouse patterns, orchestration.',
    sections: [
      { heading: 'Resources', items: ['[Data Engineering Roadmap](https://roadmap.sh/data-engineer)'] }
    ]
  },
  'spark-basics': {
    id: 'spark-basics',
    title: 'Apache Spark Basics',
    summary: 'RDD/DataFrame, transformations/actions, partitions, tuning.',
    sections: [
      { heading: 'Resources', items: ['[Spark Docs](https://spark.apache.org/docs/latest/)'] }
    ]
  },
  'airflow-basics': {
    id: 'airflow-basics',
    title: 'Airflow Basics',
    summary: 'DAGs, operators, scheduling, XCom, observability.',
    sections: [
      { heading: 'Resources', items: ['[Airflow Docs](https://airflow.apache.org/docs/)'] }
    ]
  },
  'mobile-react-native-basics': {
    id: 'mobile-react-native-basics',
    title: 'React Native Basics',
    summary: 'Components, navigation, native modules, performance basics.',
    sections: [
      { heading: 'Resources', items: ['[React Native](https://reactnative.dev/docs/getting-started)'] }
    ]
  },
  'cybersecurity-basics': {
    id: 'cybersecurity-basics',
    title: 'Cybersecurity Basics',
    summary: 'Threat models, encryption, network security, incident response.',
    sections: [
      { heading: 'Resources', items: ['[NIST CSF](https://www.nist.gov/cyberframework)'] }
    ]
  }
};

export default topics;
